import { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, User, Trash2, BookOpen, ChevronDown, ChevronUp, Loader2, Database, CheckCircle, AlertCircle, Zap, Lock, Unlock } from 'lucide-react';
import { ChatService, type ChatMessage, type LLMProvider } from '../services/chatService';
import { ContentService, type LearningModule, type ResearchDocument, type PromptExample, type ProcessingProgress } from '../services/contentService';
import { RateLimitService } from '../services/rateLimitService';
import { researchDocuments } from '../services/researchService';
import { prompts } from '../data/prompts';

export default function ChatInterface() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [provider, setProvider] = useState<LLMProvider>('anthropic');
  const [expandedSources, setExpandedSources] = useState<Set<number>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatService = ChatService.getInstance();

  // Content processing state
  const [showProcessing, setShowProcessing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState<ProcessingProgress | null>(null);
  const [currentItem, setCurrentItem] = useState<string>('');
  const [stats, setStats] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const contentService = ContentService.getInstance();

  // Rate limiting state
  const [rateLimitStatus, setRateLimitStatus] = useState(() => RateLimitService.getInstance().getStatus());
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const rateLimitService = RateLimitService.getInstance();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load stats on mount to check if content is already processed
  useEffect(() => {
    const currentStats = contentService.getStats();
    if (currentStats.totalDocuments > 0) {
      setStats(currentStats);
    }
  }, []);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    // Check rate limit
    if (!rateLimitService.canMakeRequest()) {
      setShowUnlockModal(true);
      return;
    }

    const userInput = input;
    setInput('');
    setIsLoading(true);

    try {
      const response = await chatService.sendMessage(userInput, provider);
      setMessages(chatService.getHistory());

      // Record the request and update status
      rateLimitService.recordRequest();
      setRateLimitStatus(rateLimitService.getStatus());
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnlock = () => {
    if (rateLimitService.unlock(unlockPassword)) {
      setRateLimitStatus(rateLimitService.getStatus());
      setShowUnlockModal(false);
      setUnlockPassword('');
      setUnlockError('');
    } else {
      setUnlockError('Incorrect password. Please try again.');
    }
  };

  const handleClear = () => {
    if (confirm('Clear all chat history?')) {
      chatService.clearHistory();
      setMessages([]);
    }
  };

  const toggleSources = (messageIndex: number) => {
    const newExpanded = new Set(expandedSources);
    if (newExpanded.has(messageIndex)) {
      newExpanded.delete(messageIndex);
    } else {
      newExpanded.add(messageIndex);
    }
    setExpandedSources(newExpanded);
  };

  // Define all learning modules
  const allModules: LearningModule[] = [
    { id: 'module-01', number: 1, title: 'Introduction to Generative AI', description: '', content: '', duration: '30 min', level: 'Beginner', filepath: '/content/lessons/01-introduction-to-generative-ai.md' },
    { id: 'module-02', number: 2, title: 'Getting Started with ChatGPT & Claude', description: '', content: '', duration: '35 min', level: 'Beginner', filepath: '/content/lessons/02-getting-started-with-chatgpt-claude.md' },
    { id: 'module-03', number: 3, title: 'Prompt Engineering for OTs', description: '', content: '', duration: '40 min', level: 'Intermediate', filepath: '/content/lessons/03-prompt-engineering-for-ots.md' },
    { id: 'module-04', number: 4, title: 'AI for Clinical Documentation', description: '', content: '', duration: '45 min', level: 'Intermediate', filepath: '/content/lessons/04-clinical-documentation-with-ai.md' },
    { id: 'module-05', number: 5, title: 'Treatment Planning with AI', description: '', content: '', duration: '40 min', level: 'Intermediate', filepath: '/content/lessons/05-treatment-planning-with-ai.md' },
    { id: 'module-06', number: 6, title: 'Creating Patient Education Materials', description: '', content: '', duration: '35 min', level: 'Intermediate', filepath: '/content/lessons/06-creating-patient-education-materials.md' },
    { id: 'module-07', number: 7, title: 'Research & Evidence Synthesis', description: '', content: '', duration: '35 min', level: 'Advanced', filepath: '/content/lessons/07-research-evidence-synthesis.md' },
    { id: 'module-08', number: 8, title: 'Ethics, HIPAA & Best Practices', description: '', content: '', duration: '40 min', level: 'Advanced', filepath: '/content/lessons/08-ethics-hipaa-best-practices.md' }
  ];

  // Process all content (modules, research, prompts)
  const processAllContent = async () => {
    setProcessing(true);
    setError(null);
    setCurrentItem('');

    try {
      // Step 1: Process Learning Modules (8 modules)
      setCurrentItem('Processing Learning Modules...');
      for (let i = 0; i < allModules.length; i++) {
        const module = allModules[i];
        setCurrentItem(`Processing Module ${module.number}/8: ${module.title}`);

        const content = await contentService.loadModuleFromFile(module.filepath);
        module.content = content;

        await contentService.processModule(module, (progressUpdate) => {
          setProgress(progressUpdate);
        });

        console.log(`✅ Completed ${module.title}`);
      }

      // Step 2: Process Research Documents (6 documents)
      setCurrentItem('Processing Research Documents...');
      for (let i = 0; i < researchDocuments.length; i++) {
        const doc = researchDocuments[i];
        setCurrentItem(`Processing Research ${i + 1}/6: ${doc.title}`);

        const content = await contentService.loadModuleFromFile(doc.filepath);
        const researchDoc: ResearchDocument = {
          id: doc.id,
          title: doc.title,
          description: doc.description,
          content: content,
          category: doc.category,
          filepath: doc.filepath
        };

        await contentService.processResearchDocument(researchDoc, (progressUpdate) => {
          setProgress(progressUpdate);
        });

        console.log(`✅ Completed ${doc.title}`);
      }

      // Step 3: Process Prompt Examples
      setCurrentItem('Processing Prompt Library...');
      for (let i = 0; i < prompts.length; i++) {
        const prompt = prompts[i];
        setCurrentItem(`Processing Prompt ${i + 1}/${prompts.length}: ${prompt.title}`);

        const promptExample: PromptExample = {
          id: prompt.id,
          title: prompt.title,
          description: prompt.description,
          prompt: prompt.prompt,
          category: prompt.category,
          content: `${prompt.title}\n\n${prompt.description}\n\nCategory: ${prompt.category}\nDifficulty: ${prompt.difficulty}\nTags: ${prompt.tags.join(', ')}\n\n${prompt.prompt}`
        };

        await contentService.processPromptExample(promptExample, (progressUpdate) => {
          setProgress(progressUpdate);
        });

        console.log(`✅ Completed ${prompt.title}`);
      }

      // Get final stats
      const finalStats = contentService.getStats();
      setStats(finalStats);
      setCurrentItem('✅ All content processed successfully!');
      setProgress({ stage: 'completed', progress: 100, message: 'All content complete!' });

      console.log('🎉 Content processing complete!', finalStats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Content processing failed');
      console.error('Content processing error:', err);
    } finally {
      setProcessing(false);
    }
  };

  const getProgressColor = () => {
    if (progress?.stage === 'error') return 'text-red-600';
    if (progress?.stage === 'completed') return 'text-emerald-600';
    return 'text-blue-600';
  };

  const getProgressIcon = () => {
    if (progress?.stage === 'error') return <AlertCircle className="w-5 h-5" />;
    if (progress?.stage === 'completed') return <CheckCircle className="w-5 h-5" />;
    return <Zap className="w-5 h-5 animate-pulse" />;
  };

  const suggestedQuestions = [
    "What is generative AI?",
    "How can AI help with clinical documentation?",
    "What are the limitations of AI in healthcare?",
    "How do I get started with ChatGPT?",
    "What are the HIPAA considerations for using AI?"
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-neutral-900 via-blue-950 to-neutral-900 rounded-3xl p-12 border border-neutral-800">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-3xl font-bold text-white mb-3">AI Tutor</h2>
            <p className="text-neutral-300 text-lg mb-4">
              Ask questions about the learning materials and get instant, context-aware answers
            </p>

            {/* Rate Limit Status */}
            {!rateLimitStatus.isUnlocked && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg">
                <Lock className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-neutral-300">
                  {rateLimitStatus.questionsRemaining} questions remaining today
                </span>
              </div>
            )}
            {rateLimitStatus.isUnlocked && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/20 border border-emerald-400/30 rounded-lg">
                <Unlock className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-emerald-300 font-medium">
                  Unlimited Access
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Provider Selector */}
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value as LLMProvider)}
              className="px-4 py-2 bg-white/10 text-white border border-white/20 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 [&>option]:bg-neutral-800 [&>option]:text-white"
            >
              <option value="anthropic" className="bg-neutral-800 text-white">Claude Sonnet 4.5</option>
              <option value="openai" className="bg-neutral-800 text-white">GPT-5</option>
            </select>

            {messages.length > 0 && (
              <button
                onClick={handleClear}
                className="p-2 bg-white/10 text-white border border-white/20 rounded-xl hover:bg-white/20 transition-colors"
                title="Clear chat"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content Processing Section */}
      {!stats && (
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl border-2 border-blue-200 p-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2 flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-600" />
                Process All Content
              </h3>
              <p className="text-neutral-600 text-sm">
                Load all learning modules, research documents, and prompts into the AI Tutor's knowledge base ({allModules.length + researchDocuments.length + prompts.length} items total)
              </p>
            </div>
            <button
              onClick={processAllContent}
              disabled={processing}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl hover:from-blue-700 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md font-medium"
            >
              <Database className="w-4 h-4" />
              {processing ? 'Processing...' : 'Start Processing'}
            </button>
          </div>

          {/* Progress Display */}
          {processing && currentItem && (
            <div className="space-y-3 mt-6 pt-6 border-t border-blue-200">
              <div className="flex items-center gap-3">
                <div className={getProgressColor()}>
                  {getProgressIcon()}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-neutral-700">
                      {currentItem}
                    </span>
                    {progress && (
                      <span className={`text-sm font-semibold ${getProgressColor()}`}>
                        {progress.progress}%
                      </span>
                    )}
                  </div>
                  {progress && (
                    <div className="w-full bg-neutral-200 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-500 ${
                          progress.stage === 'error'
                            ? 'bg-red-500'
                            : progress.stage === 'completed'
                            ? 'bg-emerald-500'
                            : 'bg-blue-500'
                        }`}
                        style={{ width: `${progress.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
              {progress && (
                <p className="text-sm text-neutral-600 ml-8">
                  {progress.message}
                </p>
              )}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-900 mb-1">Error Processing Content</p>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats Display (after processing complete) */}
      {stats && (
        <div className="bg-white rounded-2xl border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Content Loaded Successfully
            </h3>
            <button
              onClick={() => {
                if (confirm('Clear all processed content? This will require re-processing to use the AI Tutor.')) {
                  contentService.clearAll();
                  setStats(null);
                  setProgress(null);
                  setCurrentItem('');
                  setError(null);
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-xl hover:bg-red-100 hover:border-red-300 transition-colors text-sm"
            >
              <Trash2 className="w-4 h-4" />
              Clear Data
            </button>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <div className="flex items-center gap-2 mb-2">
                <Database className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Documents</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{stats.totalDocuments}</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-900">Chunks</span>
              </div>
              <p className="text-2xl font-bold text-emerald-600">{stats.totalChunks}</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-900">Embeddings</span>
              </div>
              <p className="text-2xl font-bold text-purple-600">{stats.totalEmbeddings}</p>
            </div>
          </div>

          <p className="text-xs text-neutral-600 mt-4">
            The AI Tutor is now ready to answer questions using all learning materials, research documents, and prompts.
          </p>
        </div>
      )}

      {/* Chat Container */}
      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
        {/* Messages */}
        <div className="h-[600px] overflow-y-auto p-6 space-y-6">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center mb-6">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-neutral-900 mb-3">
                Start a conversation
              </h3>
              <p className="text-neutral-600 mb-8 max-w-md">
                Ask me anything about the learning materials. I'll search the content and provide contextual answers with sources.
              </p>

              {/* Suggested Questions */}
              <div className="w-full max-w-2xl">
                <p className="text-sm font-semibold text-neutral-700 mb-3 text-left">
                  Suggested questions:
                </p>
                <div className="grid gap-2">
                  {suggestedQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => setInput(question)}
                      className="px-4 py-3 bg-gradient-to-br from-neutral-700 to-neutral-800 hover:from-neutral-600 hover:to-neutral-700 border border-neutral-600 rounded-xl text-left text-sm text-white transition-all"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {message.role === 'assistant' && (
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                  )}

                  <div className={`flex-1 max-w-3xl ${message.role === 'user' ? 'flex justify-end' : ''}`}>
                    <div
                      className={`rounded-2xl p-4 ${
                        message.role === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gradient-to-br from-neutral-800 to-neutral-900 border border-neutral-700 text-white'
                      }`}
                    >
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>

                      {/* Sources */}
                      {message.sources && message.sources.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-neutral-600">
                          <button
                            onClick={() => toggleSources(index)}
                            className="flex items-center gap-2 text-xs font-semibold text-neutral-300 hover:text-white transition-colors"
                          >
                            <BookOpen className="w-3.5 h-3.5" />
                            {message.sources.length} Source{message.sources.length > 1 ? 's' : ''}
                            {expandedSources.has(index) ? (
                              <ChevronUp className="w-3.5 h-3.5" />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5" />
                            )}
                          </button>

                          {expandedSources.has(index) && (
                            <div className="mt-3 space-y-2">
                              {message.sources.map((source, sIdx) => (
                                <div
                                  key={sIdx}
                                  className="p-3 bg-neutral-700/50 border border-neutral-600 rounded-lg"
                                >
                                  <div className="flex justify-between items-start mb-2">
                                    <span className="text-xs font-medium text-neutral-400">
                                      Chunk {source.chunkIndex + 1}
                                    </span>
                                    <span className="text-xs font-semibold text-blue-400">
                                      {(source.similarity * 100).toFixed(1)}% match
                                    </span>
                                  </div>
                                  <p className="text-xs text-neutral-300 leading-relaxed">
                                    {source.text.substring(0, 200)}
                                    {source.text.length > 200 ? '...' : ''}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {message.role === 'user' && (
                    <div className="w-8 h-8 bg-neutral-200 rounded-lg flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-neutral-600" />
                    </div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="flex gap-4 justify-start">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                  </div>
                  <div className="bg-gradient-to-br from-neutral-800 to-neutral-900 border border-neutral-700 rounded-2xl p-4 min-w-[100px]">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span className="text-xs text-neutral-400">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="border-t border-neutral-200 bg-neutral-50 p-4">
          <div className="flex gap-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder="Ask a question about the learning materials..."
              disabled={isLoading}
              className="flex-1 px-4 py-3 border border-neutral-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl hover:from-blue-700 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md flex items-center gap-2 font-medium"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Unlock Modal */}
      {showUnlockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Lock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-neutral-900">Daily Limit Reached</h3>
                <p className="text-sm text-neutral-600">500 questions per day</p>
              </div>
            </div>

            <p className="text-neutral-700 mb-6">
              You've reached the daily limit of 500 AI questions. This limit helps prevent abuse while keeping the service free for everyone.
            </p>

            <p className="text-sm text-neutral-600 mb-6">
              The limit will reset at <strong>{rateLimitStatus.resetTime}</strong>.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm font-semibold text-blue-900 mb-2">
                Have unlimited access?
              </p>
              <p className="text-xs text-blue-700 mb-3">
                Enter your password to unlock unlimited questions.
              </p>

              <input
                type="password"
                value={unlockPassword}
                onChange={(e) => setUnlockPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleUnlock()}
                placeholder="Enter password"
                className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm mb-2"
              />

              {unlockError && (
                <p className="text-xs text-red-600 mb-2">{unlockError}</p>
              )}

              <button
                onClick={handleUnlock}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Unlock Access
              </button>
            </div>

            <button
              onClick={() => {
                setShowUnlockModal(false);
                setUnlockPassword('');
                setUnlockError('');
              }}
              className="w-full px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
